// CHG-132 Phase E REQ-PA-023/024: 보관 접근 기록 테이블
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Filter, Check, X, Clock, KeyRound } from 'lucide-react';
import {
  listAccessRequests,
  listOrgAccessRequests,
  type AccessRequestWithUsers,
  type AccessRequestFilters,
} from '../../services/spaceAccessRequestService';
import { useDebounce } from '../../hooks/useDebounce';
import Spinner from '../ui/Spinner';
import SingleSelectDropdown from '../common/SingleSelectDropdown';

type Scope = { kind: 'project'; projectId: string } | { kind: 'org'; orgId: string };

interface Props {
  scope: Scope;
  showProjectColumn?: boolean; // org scope에서만 true
}

const STATUS_OPTIONS: { value: AccessRequestFilters['status']; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'PENDING', label: '대기' },
  { value: 'APPROVED', label: '승인' },
  { value: 'REJECTED', label: '거부' },
  { value: 'CANCELED', label: '취소' },
];

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    PENDING: { label: '대기', cls: 'bg-amber-100 text-amber-800', icon: <Clock className="size-3" /> },
    APPROVED: { label: '승인', cls: 'bg-green-100 text-green-800', icon: <Check className="size-3" /> },
    REJECTED: { label: '거부', cls: 'bg-red-100 text-red-700', icon: <X className="size-3" /> },
    CANCELED: { label: '취소', cls: 'bg-gray-100 text-gray-700', icon: null },
  };
  const m = map[status] ?? map.PENDING;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded ${m.cls}`}>
      {m.icon}
      {m.label}
    </span>
  );
}

export default function AccessRequestHistory({ scope, showProjectColumn = false }: Props) {
  const [status, setStatus] = useState<AccessRequestFilters['status']>('ALL');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const filters: AccessRequestFilters = useMemo(() => {
    const f: AccessRequestFilters = { status };
    if (debouncedSearch.trim()) f.q = debouncedSearch.trim();
    if (from) f.from = new Date(from).toISOString();
    if (to) {
      // to 날짜의 23:59:59까지 포함
      const t = new Date(to);
      t.setHours(23, 59, 59, 999);
      f.to = t.toISOString();
    }
    return f;
  }, [status, debouncedSearch, from, to]);

  const queryKey =
    scope.kind === 'project'
      ? ['access-requests', 'project', scope.projectId, filters]
      : ['access-requests', 'org', scope.orgId, filters];

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () =>
      scope.kind === 'project'
        ? listAccessRequests(scope.projectId, filters)
        : listOrgAccessRequests(scope.orgId, filters),
    retry: false,
  });

  if (isError) {
    const err = error as { response?: { status?: number } };
    return (
      <p className="text-sm text-red-500 py-4">
        {err.response?.status === 403
          ? '접근 기록을 조회할 권한이 없습니다.'
          : '접근 기록을 불러오지 못했습니다.'}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* 필터 바 */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-lg p-3">
        <div className="flex items-center gap-1.5">
          <Filter className="size-3.5 text-gray-400" />
          <SingleSelectDropdown
            label="상태"
            value={status ?? 'ALL'}
            onChange={(v) => setStatus(v as AccessRequestFilters['status'])}
            clearable={false}
            options={STATUS_OPTIONS.map((opt) => ({ value: opt.value ?? 'ALL', label: opt.label }))}
          />
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="요청자/승인자 검색"
            className="pl-8 pr-3 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-500">
          <span>기간:</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="text-sm border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <span>~</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="text-sm border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* 결과 테이블 */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner className="size-6" />
          </div>
        ) : !data || data.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-sm text-gray-500">
            <KeyRound className="size-8 text-gray-300 mb-2" />
            접근 요청 기록이 없습니다.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-sm text-gray-500 uppercase">
                <th className="px-4 py-2 text-left font-medium">요청자</th>
                {showProjectColumn && <th className="px-4 py-2 text-left font-medium">프로젝트</th>}
                <th className="px-4 py-2 text-left font-medium">상태</th>
                <th className="px-4 py-2 text-left font-medium">승인자</th>
                <th className="px-4 py-2 text-left font-medium">요청 사유</th>
                <th className="px-4 py-2 text-left font-medium">요청</th>
                <th className="px-4 py-2 text-left font-medium">처리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((r: AccessRequestWithUsers) => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-2.5">
                    {r.requester ? (
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{r.requester.name}</p>
                        <p className="text-sm text-gray-500 truncate">{r.requester.email}</p>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">탈퇴한 사용자</span>
                    )}
                  </td>
                  {showProjectColumn && (
                    <td className="px-4 py-2.5">
                      {r.project ? (
                        <div className="min-w-0">
                          <p className="text-gray-900 truncate">{r.project.name}</p>
                          <p className="text-sm text-gray-500">{r.project.projectKey}</p>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-2.5">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-4 py-2.5 text-gray-700">
                    {r.approver?.name ?? <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-gray-700 max-w-xs">
                    <p className="line-clamp-2" title={r.reason}>{r.reason}</p>
                    {r.decisionNote && (
                      <p className="text-sm text-gray-500 mt-1 italic line-clamp-1" title={r.decisionNote}>
                        메모: {r.decisionNote}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-500 whitespace-nowrap">
                    {new Date(r.createdAt).toLocaleString('ko-KR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-500 whitespace-nowrap">
                    {r.decidedAt
                      ? new Date(r.decidedAt).toLocaleString('ko-KR', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
